/* Enhance the app's existing picker without replacing pricing, inputs, or order actions. */
(() => {
  const observed = new WeakSet();

  function initialize() {
    document.querySelectorAll('[data-mural-customizer]').forEach(app => {
      if (observed.has(app)) return;
      observed.add(app);

      function updateBadges() {
        let pricing;
        let translations;
        try {
          pricing = JSON.parse(app.querySelector('[data-pricing-json]')?.textContent || '{}');
          translations = JSON.parse(app.querySelector('[data-i18n]')?.textContent || '{}');
        } catch {
          return;
        }

        const picker = app.querySelector('.mural-cta');
        if (!picker) return;
        const titles = picker.querySelectorAll('.mural-cta__section-title');
        ['Choose material', 'Enter wall size'].forEach((label, index) => {
          const title = titles[index];
          if (!title) return;
          if (title.textContent !== label) title.textContent = label;
          title.setAttribute('role', 'heading');
          title.setAttribute('aria-level', '2');
        });
        const materials = picker.querySelector('.mural-cta__materials');
        if (!materials) return;
        if (!picker.querySelector('.z-mural-guide')) {
          const guide = document.createElement('button');
          guide.type = 'button';
          guide.className = 'z-mural-guide';
          guide.textContent = 'Material guide';
          guide.addEventListener('click', () => {
            picker.querySelector('.mural-cta__material.is-selected .mural-matinfo-btn')?.click();
          });
          titles[0]?.parentElement.append(guide);
        }
        let description = picker.querySelector('.z-mural-description');
        if (!description) {
          description = document.createElement('div');
          description.className = 'z-mural-description';
          description.setAttribute('aria-live', 'polite');
          materials.after(description);
        }
        const selectedName = picker.querySelector('.mural-cta__material.is-selected .mural-cta__material-name')?.textContent.trim();
        const selected = pricing.materials?.find(item => item.label === selectedName);
        const materialCopy = name => {
          if (/non[ -]?woven/i.test(name || '')) return { hint: 'Paste required · Smooth matte finish', detail: 'Apply wallpaper paste to the wall, then hang each panel. This material is not self-adhesive.' };
          if (/peel/i.test(name || '')) return { hint: 'Self-adhesive · No paste needed', detail: 'Peel off the backing and apply to a clean, smooth surface. Wallpaper paste is not needed.' };
          if (/textured/i.test(name || '')) return { hint: 'Paste required · Textured finish', detail: 'A textured finish that requires wallpaper paste. Check the Material guide for installation details.' };
          return null;
        };
        const descriptionText = materialCopy(selectedName)?.detail || selected?.description || '';
        if (description.dataset.copy !== descriptionText) {
          const heading = document.createElement('strong');
          heading.textContent = 'Installation';
          const detail = document.createElement('span');
          detail.textContent = descriptionText;
          description.replaceChildren(heading, detail);
          description.dataset.copy = descriptionText;
        }
        description.hidden = !descriptionText;

        const dims = picker.querySelector('.mural-cta__dims');
        if (dims && !picker.querySelector('.z-mural-dimension-labels')) {
          const labels = document.createElement('div');
          labels.className = 'z-mural-dimension-labels';
          labels.setAttribute('aria-hidden', 'true');
          labels.innerHTML = '<span>Width</span><span>Height</span>';
          dims.before(labels);
          const help = document.createElement('p');
          help.className = 'z-mural-size-help';
          help.textContent = 'Enter your wall dimensions to see the total price. You can check the design in the next step.';
          dims.after(help);
        }
        picker.querySelectorAll('[data-pcta-dim]').forEach(input => {
          const dimension = input.dataset.pctaDim;
          const unit = dimension.endsWith('_cm') ? 'cm' : dimension.endsWith('_ft') ? 'feet' : 'inches';
          input.setAttribute('aria-label', `${dimension.startsWith('w_') ? 'Wall width' : 'Wall height'} in ${unit}`);
        });
        picker.querySelectorAll('[data-pcta-unit]').forEach(button => {
          button.setAttribute('aria-pressed', String(button.classList.contains('is-active')));
        });
        const coverage = picker.querySelector('.mural-cta__coverage');
        if (coverage && !picker.querySelector('.z-mural-made')) {
          const made = document.createElement('div');
          made.className = 'z-mural-made';
          made.innerHTML = '<span>Made to measure</span><span>Free shipping</span>';
          coverage.after(made);
        }
        const sample = picker.querySelector('[data-mural-sample]');
        if (sample && !sample.classList.contains('z-roll-sample-toggle')) {
          sample.classList.add('z-roll-sample-toggle');
          sample.setAttribute('aria-haspopup', 'dialog');
          sample.setAttribute('aria-label', 'Order a sample');
          sample.innerHTML = '<span class="z-sample-invite-icon" aria-hidden="true"><svg viewBox="0 0 32 32" fill="none"><path d="m7 10 11-5 8 4v17l-11 4-8-4V10Z"/><path d="m7 10 8 4 11-5M15 14v16M3 5l5-2M3 11V5"/></svg></span><span class="z-sample-invite-copy"><span class="z-sample-invite-kicker">TRY IT AT HOME</span><strong>Start with a sample</strong><small>See the colour. Feel the finish. Choose with confidence.</small></span><span class="z-sample-invite-action">Order a sample</span>';
        }
        const action = picker.querySelector('[data-open-customizer]');
        if (action && !action.querySelector('.z-mural-arrow')) {
          const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          icon.setAttribute('class', 'z-mural-arrow');
          icon.setAttribute('viewBox', '0 0 24 24');
          icon.setAttribute('aria-hidden', 'true');
          icon.innerHTML = '<path d="M4 12h15m-6-6 6 6-6 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>';
          action.append(icon);
        }
        if (action && !picker.querySelector('.z-mural-preview-help')) {
          const help = document.createElement('p');
          help.className = 'z-mural-preview-help';
          help.textContent = 'Preview your design before adding it to your cart.';
          action.after(help);
        }

        app.querySelectorAll('.mural-cta__material').forEach(button => {
          button.setAttribute('aria-pressed', String(button.classList.contains('is-selected')));
          const name = button.querySelector('.mural-cta__material-name');
          if (!name) return;
          const material = pricing.materials?.find(item => item.label === name.textContent.trim());
          const existing = button.querySelector('.zenvana-material-badge');
          if (!material?.mostPopular) {
            existing?.remove();
            return;
          }
          const text = translations.mostPopular || 'Most popular';
          if (existing) {
            if (existing.textContent !== text) existing.textContent = text;
            return;
          }
          const badge = document.createElement('span');
          badge.className = 'zenvana-material-badge';
          badge.textContent = text;
          name.after(badge);
        });
      }

      new MutationObserver(updateBadges).observe(app, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      updateBadges();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
